import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { PBXExtensionListItemType, PBXExtensionType } from '../../types/pbx-extension/pbx-extension.type';
import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import {
  deletePBXExtensionDetails,
  getPBXExtensionDetails,
  getPBXExtensionsList,
  patchCredentialPBXExtensions,
  patchPBXExtensionDetails,
  postCheckPBXExtensionsExistence,
  postPBXExtensionDetails,
} from './pbxExtensions.action';
import snackbarUtils from '../../utils/SnackbarUtils';

export interface PBXExtensionsState {
  pbxExtensionsList: null | PaginationType<PBXExtensionListItemType>;
  pbxExtensionsListFetchStatus: FETCH_STATUS;
  pbxExtensionsListRouteParams: RouteParamsType;
  pbxExtensionDetails: null | PBXExtensionType;
  pbxExtensionDetailsPost: null | { success: { username: string }[]; errors: { username: string; message: string }[] };
  pbxExtensionDetailsFetchStatus: FETCH_STATUS;
  pbxExtensionDetailsPostStatus: FETCH_STATUS;
  pbxExtensionDetailsPatchStatus: FETCH_STATUS;
  pbxExtensionDetailsDeleteStatus: FETCH_STATUS;
  checkPBXExtensionsExistencePostStatus: FETCH_STATUS;
  checkPBXExtensionsExistencePost: null | {
    success: { username: string; id: number }[];
    errors: { username: string; message: string }[];
  };
  updatePBXExtensionsPatchStatus: FETCH_STATUS;
  updatePBXExtensions: null | { id: number };
}

export const initialPBXExtensionsRouteParams = {
  page: 1,
  limit: 10,
  order: '-obj.id',
  q: '',
  status: '',
  sip_registration_status: '',
};

const INIT_STATE: PBXExtensionsState = {
  pbxExtensionsList: null,
  pbxExtensionsListFetchStatus: FETCH_STATUS.NULL,
  pbxExtensionsListRouteParams: initialPBXExtensionsRouteParams,
  pbxExtensionDetails: null,
  pbxExtensionDetailsPost: null,
  pbxExtensionDetailsFetchStatus: FETCH_STATUS.NULL,
  pbxExtensionDetailsPostStatus: FETCH_STATUS.NULL,
  pbxExtensionDetailsPatchStatus: FETCH_STATUS.NULL,
  pbxExtensionDetailsDeleteStatus: FETCH_STATUS.NULL,
  checkPBXExtensionsExistencePostStatus: FETCH_STATUS.NULL,
  checkPBXExtensionsExistencePost: null,
  updatePBXExtensionsPatchStatus: FETCH_STATUS.NULL,
  updatePBXExtensions: null,
};

const pbxExtensionsSlice = createSlice({
  name: 'pbx-extensions',
  initialState: INIT_STATE,
  reducers: {
    setPBXExtensionsList: (state, action) => {
      state.pbxExtensionsList = action.payload;
    },
    setPBXExtensionsListFetchStatus: (state, action) => {
      state.pbxExtensionsListFetchStatus = action.payload;
    },
    setPBXExtensionDetails: (state, action) => {
      state.pbxExtensionDetails = action.payload;
    },
    setPBXExtensionDetailsFetchStatus: (state, action) => {
      state.pbxExtensionDetailsFetchStatus = action.payload;
    },
    setPBXExtensionDetailsDeleteStatus: (state, action) => {
      state.pbxExtensionDetailsDeleteStatus = action.payload;
    },
    setPBXExtensionDetailsPatchStatus: (state, action) => {
      state.pbxExtensionDetailsPatchStatus = action.payload;
    },
    setPBXExtensionDetailsPostStatus: (state, action) => {
      state.pbxExtensionDetailsPostStatus = action.payload;
    },
    setPBXExtensionDetailsPost: (state, action) => {
      state.pbxExtensionDetailsPost = action.payload;
    },
    setCheckPBXExtensionsExistencePost: (state, action) => {
      state.checkPBXExtensionsExistencePost = action.payload;
    },
    setCheckPBXExtensionsExistencePostStatus: (state, action) => {
      state.checkPBXExtensionsExistencePostStatus = action.payload;
    },
    setUpdatePBXExtensions: (state, action) => {
      state.updatePBXExtensions = action.payload;
    },
    setUpdatePBXExtensionsPatchStatus: (state, action) => {
      state.updatePBXExtensionsPatchStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'pbx-extensions') {
        state.pbxExtensionsListRouteParams = { ...state.pbxExtensionsListRouteParams, ...payload.data };
      }
    });

    // CREATE PBX EXTENSION DETAILS
    builder.addCase(postPBXExtensionDetails.pending, state => {
      state.pbxExtensionDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postPBXExtensionDetails.rejected, (state, action) => {
      state.pbxExtensionDetailsPostStatus = FETCH_STATUS.REJECTED;
      snackbarUtils.error(action.payload as string);
    });
    builder.addCase(postPBXExtensionDetails.fulfilled, (state, action) => {
      state.pbxExtensionDetailsPostStatus = FETCH_STATUS.FULFILLED;
      state.pbxExtensionDetailsPost = action.payload;
    });

    // DELETE PBX EXTENSION DETAILS
    builder.addCase(deletePBXExtensionDetails.pending, state => {
      state.pbxExtensionDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deletePBXExtensionDetails.rejected, (state, action) => {
      state.pbxExtensionDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deletePBXExtensionDetails.fulfilled, state => {
      state.pbxExtensionDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('PBX Extension deleted successfully');
      });
    });

    // PATCH PBX EXTENSION DETAILS
    builder.addCase(patchPBXExtensionDetails.pending, state => {
      state.pbxExtensionDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchPBXExtensionDetails.rejected, (state, action) => {
      state.pbxExtensionDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchPBXExtensionDetails.fulfilled, state => {
      state.pbxExtensionDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('PBX Extension updated successfully');
      });
    });

    // GET PBX EXTENSIONS LIST
    builder.addCase(getPBXExtensionsList.pending, state => {
      state.pbxExtensionsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getPBXExtensionsList.rejected, (state, action) => {
      if (action.payload) {
        state.pbxExtensionsListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error(action.payload as string));
      }
    });
    builder.addCase(getPBXExtensionsList.fulfilled, (state, action) => {
      state.pbxExtensionsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.pbxExtensionsList = action.payload;
    });

    // GET PBX EXTENSIONS LIST
    builder.addCase(getPBXExtensionDetails.pending, state => {
      state.pbxExtensionDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getPBXExtensionDetails.rejected, (state, action) => {
      if (action.payload) {
        state.pbxExtensionDetailsFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error(action.payload as string));
      }
    });
    builder.addCase(getPBXExtensionDetails.fulfilled, (state, action) => {
      state.pbxExtensionDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.pbxExtensionDetails = action.payload;
    });

    // CHECK PBX EXTENSIONS
    builder.addCase(postCheckPBXExtensionsExistence.pending, state => {
      state.checkPBXExtensionsExistencePostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postCheckPBXExtensionsExistence.rejected, (state, action) => {
      if (action.payload) {
        state.checkPBXExtensionsExistencePostStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error(action.payload as string));
      }
    });
    builder.addCase(postCheckPBXExtensionsExistence.fulfilled, (state, action) => {
      state.checkPBXExtensionsExistencePostStatus = FETCH_STATUS.FULFILLED;
      state.checkPBXExtensionsExistencePost = action.payload;
    });

    // UPDATE PBX EXTENSIONS
    builder.addCase(patchCredentialPBXExtensions.pending, state => {
      state.updatePBXExtensionsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchCredentialPBXExtensions.rejected, (state, action) => {
      if (action.payload) {
        state.updatePBXExtensionsPatchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error(action.payload as string));
      }
    });
    builder.addCase(patchCredentialPBXExtensions.fulfilled, (state, action) => {
      state.updatePBXExtensionsPatchStatus = FETCH_STATUS.FULFILLED;
      state.updatePBXExtensions = action.payload;
    });
  },
});

export const {
  setPBXExtensionsList,
  setPBXExtensionsListFetchStatus,
  setPBXExtensionDetailsFetchStatus,
  setPBXExtensionDetails,
  setPBXExtensionDetailsPatchStatus,
  setPBXExtensionDetailsPostStatus,
  setPBXExtensionDetailsDeleteStatus,
  setPBXExtensionDetailsPost,
  setCheckPBXExtensionsExistencePostStatus,
  setCheckPBXExtensionsExistencePost,
  setUpdatePBXExtensionsPatchStatus,
  setUpdatePBXExtensions,
} = pbxExtensionsSlice.actions;

export default pbxExtensionsSlice.reducer;
